import { defineComponent, withDirectives, resolveDirective } from 'vue';
import { QInput } from 'quasar';
import BaseComponent from './baseFormMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import { toggleNativeDatePicker, dismissNativeDatePicker } from './nativeDatePickerToggle.js';

import { appSettings } from '@/Shared/appSettings.js';
import TranslationContainer from './../TranslationContainer.jsx';

const datetimeregex1 = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d/;
const datetimeregex2 = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d/;
const datetimeregex3 = /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/;

export default defineComponent({
    name: 'sform-datepicker',
    mixins: [BaseComponent],
    components: {
        TranslationContainer,
        QInput,
    },
    data: () => ({
        nativePickerOpen: false,
        nativePickerSuppressUntil: 0,
    }),
    props: {
    },
    created() {
    },
    mounted() {
        this.loadComplete();
    },
    computed: {
        itemvalue_aslocaldate() {
            // Cannot use ISO format directly "2200-07-22T06:00:00Z"
            // The format is "yyyy-MM-dd"
            if (!this.itemvalue)
                return null;

            const dt = new Date(Date.parse(this.itemvalue));
            const year = dt.getFullYear();
            const month = (dt.getMonth() + 1).toString();
            const day = (dt.getDate()).toString();

            const str = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

            utils.debug(`DatePicker reading date from model value:'${this.itemvalue}' as '${str}'`);

            return str;
        },
        itemvalue_aslocaltime() {
            // Cannot use ISO format directly "2200-07-22T06:00:00Z"
            // The format is "hh:mm" followed by optional ":ss" or ":ss.SSS".
            if (!this.itemvalue)
                return null;

            let str;
            if (this.itemvalue.match(datetimeregex1) || this.itemvalue.match(datetimeregex2) || this.itemvalue.match(datetimeregex3)) {
                const dt = new Date(Date.parse(this.itemvalue));
                const hour = (dt.getHours()).toString();
                const minute = (dt.getMinutes()).toString();
                const second = (dt.getSeconds()).toString();

                str = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}`;
            }
            else {
                // Assume HH:MM[:SS] (seconds are optional)
                const dt = this.itemvalue.split(':');
                const hour = dt.length > 0 ? dt[0] : '00';
                const minute = dt.length > 1 ? dt[1] : '00';
                const second = dt.length > 2 ? dt[2] : '00';

                str = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}`;
            }
            utils.debug(`DatePicker reading time from model value:'${this.itemvalue}' as '${str}'`);

            return str;
        },
        // Same defensive pattern as formSingleDatePicker.jsx / formDateRangePicker.jsx
        // (CCDEV-18145): QField/native input model-value here is a locally-formatted
        // display string, not necessarily the raw stored value. Validate this.itemvalue
        // directly so a future stricter format/pattern check can't silently fail here.
        rules() {
            const r = [];
            if (this.element?.required)
                r.push(() => {
                    const v = this.itemvalue;
                    return (v === null || v === undefined || v === '') ? 'Required' : true;
                });

            if (this.element?.schema)
                r.push(() => {
                    const errors = utils.schema.performValidation(this.element.schema, this.itemvalue);
                    return errors.length > 0 ? errors[0].error : true;
                });

            return r;
        },
    },
    methods: {
        sync_fromlocaldate(value) {
            // Keep picker open-state until cancel/blur/toggle-close (see formDateTime).
            if (value == null || value === '') {
                this.sync(null);
                return;
            }
            const dt = new Date(value);
            if (Number.isNaN(dt.getTime())) {
                this.sync(null);
                return;
            }
            const year = dt.getFullYear();
            const month = (dt.getMonth() + 1).toString();
            const day = (dt.getDate()).toString();
            const str = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

            utils.debug(`DatePicker writing date to model value:'${value}' as '${str}'`);

            this.sync(str);
        },
        sync_fromlocaltime(value) {
            // Keep picker open-state until cancel/blur/toggle-close (see formDateTime).
            if (value == null || value === '') {
                this.sync(null);
                return;
            }
            // Assume HH:MM[:SS] (seconds are optional)
            const dt = value.split(':');
            const hour = dt.length > 0 ? dt[0] : '00';
            const minute = dt.length > 1 ? dt[1] : '00';
            const second = dt.length > 2 ? dt[2] : '00';
            const str = `1900-01-01T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}`;

            utils.debug(`DatePicker writing time to model value:'${value}' as '${str}'`);

            this.sync(str);
        },
        toggleNativePicker(e) {
            if (this.readonly || this.noinput || this.appearance?.readonly)
                return;
            const next = toggleNativeDatePicker(e, {
                isOpen: this.nativePickerOpen,
                suppressUntil: this.nativePickerSuppressUntil,
            }, this.dismissNativePicker);
            this.nativePickerOpen = next.isOpen;
            this.nativePickerSuppressUntil = next.suppressUntil;
        },
        dismissNativePicker() {
            const next = dismissNativeDatePicker({
                isOpen: this.nativePickerOpen,
                suppressUntil: this.nativePickerSuppressUntil,
            });
            this.nativePickerOpen = next.isOpen;
            this.nativePickerSuppressUntil = next.suppressUntil;
        },
        handleBlur(e) {
            this.dismissNativePicker();
            this.onBlur(e);
        },
    },
    render() {
        let input;

        let slots = {
            message: ({message}) => {
                return <TranslationContainer context={this} value={message}></TranslationContainer>
            }
        };
        if (this.hasInputLabel)
            slots.label = () => <TranslationContainer context={this} value={this.labelText}></TranslationContainer>;

        if (this.element.format == 'date')
            input = (
                <QInput
                    class="caption q-pa-none q-ma-none sform-input-white sform-nativedate-input"
                    style={{ width: "100%" }}
                    dense
                    outlined
                    labelSlot={this.hasInputLabel}
                    modelValue={this.itemvalue_aslocaldate}
                    ref="form"
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    type="date"
                    onUpdate:modelValue={(value) => this.sync_fromlocaldate(value)}
                    onClick={this.toggleNativePicker}
                    onCancel={this.dismissNativePicker}
                    hint={this.inputHint}
                    hide-hint
                    bottom-slots={this.showFieldBottomSlots}
                    onBlur={this.handleBlur}
                    onFocus={this.onFocus}
                    persistent-hint={appSettings.DebugTranslationPrefixSetting}
                >{slots}</QInput>
            );
        else if (this.element.format == 'time')
            // type=time: do not showPicker() on field click — opens clock popup and
            // blocks hour/minute spinbutton typing (Office Hours Open/Close Time).
            // Native clock icon still opens the picker; date format keeps click-toggle.
            input = (
                <QInput
                    class="caption q-pa-none q-ma-none sform-input-white sform-nativedate-input"
                    style={{ width: "100%" }}
                    dense
                    outlined
                    labelSlot={this.hasInputLabel}
                    modelValue={this.itemvalue_aslocaltime}
                    ref="form"
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    type="time"
                    onUpdate:modelValue={(value) => this.sync_fromlocaltime(value)}
                    hint={this.inputHint}
                    hide-hint
                    bottom-slots={this.showFieldBottomSlots}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                    persistent-hint={appSettings.DebugTranslationPrefixSetting}
                >{slots}</QInput>
            );

        if (input && this.appearance)
            utils.applyVue2ToVue3Props(input, this.appearance);

        // if (this.directives && this.directives.some(a => a.name == 'on-enter-default'))
        //    input.props.autofocus = true;

        if (this.directives) {
            var applyDirectives = this.directives.map(d => {
                const dirDef = d.directive || resolveDirective(d.name);

                return [dirDef, d.value, d.arg, d.modifiers];
            });
            input = withDirectives(input, applyDirectives);
        }

        return input;
    }
});
